import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { contentService } from '../../services/content';
import { supabase } from '../../services/supabase';
import { Verse } from '../../types/content';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import './VerseCard.css';

interface Props {
  verse: Verse;
  onDelete?: (verseId: string) => void;
  onVote?: () => void;
  /** Surface the card sits on. Dark glass for the feed pages, white for the light panels. */
  variant?: 'light' | 'dark';
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

const VerseCard: React.FC<Props> = ({ verse, onDelete, onVote, variant = 'light' }) => {
  const { authState } = useAuth();
  const {
    play,
    pause,
    isPlaying: globalIsPlaying,
    stop,
    currentAudioKey,
    currentTime,
    duration,
    formatTime,
  } = useAudioPlayer();
  const [isVoting, setIsVoting] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const isVersePlaying = globalIsPlaying && currentAudioKey === verse.id;

  useEffect(() => {
    if (!verse.file_url) {
      setAudioError('No audio file available');
      return;
    }
    if (verse.file_url.startsWith('http')) {
      setAudioUrl(verse.file_url);
      return;
    }
    const { data } = supabase.storage.from('verses').getPublicUrl(verse.file_url);
    if (data?.publicUrl) {
      setAudioUrl(data.publicUrl);
      setAudioError(null);
    } else {
      setAudioError('Unable to load audio file');
    }
  }, [verse.file_url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleLoadedMetadata = () => setAudioDuration(audio.duration);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [audioUrl]);

  const canDelete = useMemo(() => {
    const user = authState.user;
    if (!user || !onDelete || !verse.beat) return false;
    const ownsBeat =
      user.id === verse.beat.producer_id &&
      (user.role === 'producer' || user.role === 'producer_rapper');
    return ownsBeat || user.id === verse.rapper_id;
  }, [authState.user, onDelete, verse.beat, verse.rapper_id]);

  if (!verse || !verse.rapper || !verse.beat) {
    return null;
  }

  const handlePlayPause = () => {
    if (!audioUrl || !audioRef.current) {
      setAudioError('Audio file not available');
      return;
    }
    if (isVersePlaying) {
      pause();
      return;
    }
    setAudioError(null);
    play(audioRef.current, verse.id).catch(() => setAudioError('Unable to play audio file'));
  };

  const handleVote = async (value: 1 | -1) => {
    if (!authState.user) return;
    setIsVoting(true);
    try {
      if (value === 1) {
        await contentService.vote('verse', verse.id, 1);
      } else if (value === -1 && verse.user_vote === 1) {
        await contentService.deleteVote('verse', verse.id);
      }
      if (onVote) onVote();
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setIsVoting(false);
    }
  };

  const rapperName = verse.rapper.username || verse.rapper_username || 'Unknown artist';
  const producer = verse.beat.producer;
  const progress = isVersePlaying && duration > 0 ? (currentTime / duration) * 100 : 0;
  const isListener = authState.user?.role === 'listener';

  return (
    <article className={`verse-card verse-card--${variant}`}>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onEnded={() => stop()}
          onError={() => setAudioError('Error loading audio file')}
        />
      )}

      <header className="verse-card__head">
        <span className="verse-card__avatar" aria-hidden>
          {rapperName.charAt(0).toUpperCase()}
        </span>
        <div className="verse-card__identity">
          <Link to={`/profile/${verse.rapper.id}`} className="verse-card__rapper">
            {rapperName}
          </Link>
          <p className="verse-card__origin">
            on{' '}
            <Link to={`/beats/${verse.beat.id}`} className="verse-card__origin-link">
              {verse.beat.title}
            </Link>
            {producer?.username && (
              <>
                {' · '}
                <Link to={`/profile/${producer.id}`} className="verse-card__origin-link">
                  {producer.username}
                </Link>
              </>
            )}
          </p>
        </div>
        <time className="verse-card__date" dateTime={verse.created_at}>
          {formatDate(verse.created_at)}
        </time>
      </header>

      <div className="verse-card__chips">
        <span className="verse-card__chip verse-card__chip--genre">{verse.beat.genre}</span>
        <span className="verse-card__chip verse-card__chip--bpm">{verse.beat.bpm} BPM</span>
        {verse.status === 'pending' && (
          <span
            className="verse-card__chip verse-card__chip--pending"
            title="Only you and the producer can hear this until it is approved"
          >
            Waiting on the producer
          </span>
        )}
        {verse.status === 'declined' && (
          <span className="verse-card__chip verse-card__chip--declined">Not published</span>
        )}
      </div>

      <div className="verse-card__player">
        <button
          type="button"
          onClick={handlePlayPause}
          disabled={!audioUrl || !!audioError}
          className="verse-card__play"
          aria-label={isVersePlaying ? 'Pause verse' : 'Play verse'}
          title={audioError || (!audioUrl ? 'Audio not available' : 'Play / pause')}
        >
          {isVersePlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M8 5h3v14H8zM13 5h3v14h-3z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M8 5.14v13.72L19 12z" />
            </svg>
          )}
        </button>

        <div className="verse-card__track">
          <div className="verse-card__bar">
            <span className="verse-card__bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="verse-card__times">
            <span>{isVersePlaying ? formatTime(currentTime) : '0:00'}</span>
            <span>
              {isVersePlaying
                ? formatTime(duration)
                : audioDuration > 0
                  ? formatTime(audioDuration)
                  : '--:--'}
            </span>
          </div>
        </div>
      </div>

      {audioError && <p className="verse-card__error">{audioError}</p>}

      <footer className="verse-card__footer">
        {isListener ? (
          <button
            type="button"
            onClick={() => handleVote(verse.user_vote === 1 ? -1 : 1)}
            disabled={isVoting}
            className={`verse-card__vote ${verse.user_vote === 1 ? 'verse-card__vote--on' : ''}`}
            aria-label={verse.user_vote === 1 ? 'Remove vote' : 'Vote'}
          >
            <svg
              viewBox="0 0 24 24"
              fill={verse.user_vote === 1 ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" />
            </svg>
            <span>{verse.vote_count || 0}</span>
          </button>
        ) : (
          <span className="verse-card__likes">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            {verse.vote_count || 0}
          </span>
        )}

        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete?.(verse.id)}
            className="verse-card__delete"
            aria-label="Delete verse"
            title="Delete verse"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M9 3a1 1 0 00-.894.553L7.382 5H4a1 1 0 000 2h16a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0015 3H9zM6 9v9a3 3 0 003 3h6a3 3 0 003-3V9H6zm4 2a1 1 0 012 0v6a1 1 0 11-2 0v-6zm5 0a1 1 0 10-2 0v6a1 1 0 102 0v-6z" />
            </svg>
          </button>
        )}
      </footer>
    </article>
  );
};

export default VerseCard;
